import { useContext } from "react";

import { Group, Paper, SimpleGrid, Stack, UnstyledButton } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { IconEdit } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { EncounterContext } from "@/contexts/encounter";
import {
  formatTimeSpentMinutes,
  getEncounterTimeSpent,
} from "@/encounters/utils/encounter-time-spent";
import PermissionGate from "@/permission/components/PermissionGate";
import { ENCOUNTER_TIME_SPENT_EDIT_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";

import EncounterTimeSpentDrawer from "./EncounterTimeSpentDrawer";

interface EncounterTimeSpentProps {
  /** Shows the Edit action. The DX, Plan & Orders tab passes it while the encounter can be edited. */
  isEditable?: boolean;
}

/**
 * "Time Spent During Encounter" card: face-to-face, care coordination and total minutes for the
 * encounter in `EncounterContext`. Read-only unless `isEditable`, and then only for charting roles.
 */
export default function EncounterTimeSpent({ isEditable = false }: EncounterTimeSpentProps) {
  const { encounter } = useContext(EncounterContext);
  const [isDrawerOpened, drawerHandlers] = useDisclosure(false);

  if (!encounter) return null;

  const { faceToFaceMinutes, careCoordinationMinutes, totalMinutes } =
    getEncounterTimeSpent(encounter);

  return (
    <Paper
      p="lg"
      radius="sm"
      data-testid="encounter-time-spent-card"
      bd="1px solid var(--mantine-color-cream-4)"
    >
      <Stack gap="md">
        <Group justify="space-between">
          <TextWrapper fw={600} size="md" c="dark.9">
            Time Spent During Encounter
          </TextWrapper>
          {isEditable && (
            <PermissionGate
              allowPatient={false}
              requiredPermissions={ENCOUNTER_TIME_SPENT_EDIT_PERMISSION_FLAGS}
            >
              <UnstyledButton
                onClick={drawerHandlers.open}
                data-testid="encounter-time-spent-edit-button"
              >
                <Group gap="sm">
                  <IconEdit size={20} color="var(--mantine-color-gray-8)" />
                  <TextWrapper size="md" c="dark.9">
                    Edit
                  </TextWrapper>
                </Group>
              </UnstyledButton>
            </PermissionGate>
          )}
        </Group>
        <SimpleGrid spacing="md" cols={{ base: 1, sm: 3 }}>
          <TimeSpentValue
            minutes={faceToFaceMinutes}
            label="Face-to-Face w/ Client"
            testId="encounter-time-spent-face-to-face"
          />
          <TimeSpentValue
            label="Coordinating Care"
            minutes={careCoordinationMinutes}
            testId="encounter-time-spent-care-coordination"
          />
          <TimeSpentValue
            label="Total Time"
            minutes={totalMinutes}
            testId="encounter-time-spent-total"
          />
        </SimpleGrid>
      </Stack>
      {isEditable && (
        <EncounterTimeSpentDrawer opened={isDrawerOpened} onClose={drawerHandlers.close} />
      )}
    </Paper>
  );
}

const TimeSpentValue = ({
  label,
  minutes,
  testId,
}: {
  label: string;
  minutes: number | undefined;
  testId: string;
}) => (
  <Stack gap="xs">
    <TextWrapper size="sm" c="gray.7">
      {label}
    </TextWrapper>
    <TextWrapper fw={600} size="md" c="dark.9" data-testid={testId}>
      {formatTimeSpentMinutes(minutes)}
    </TextWrapper>
  </Stack>
);
